<template>
  <!-- Main Wrapper -->
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card">
          <div class="card-body">

            <!-- ══════════════════════════════════════
                 SECTION 1 · BASIC INFORMATION
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-box"></i></div>
              <div>
                <h6 class="section-title">Basic Information</h6>
                <p class="section-sub">Core product identity details</p>
              </div>
            </div>

            <div class="row g-3 mb-4">

              <!-- Category — FIRST as requested -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Category <span class="required-star">*</span></label>
                  <model-list-select
                    :list="categoryListFromApi"
                    v-model="form.itemcategory"
                    option-value="categoryid"
                    option-text="categoryname"
                    placeholder="Select category"
                    ref="categorySelect"
                    :class="{ 'is-invalid': errors.itemcategory }"
                  />
                  <div class="invalid-feedback" v-if="errors.itemcategory">{{ errors.itemcategory }}</div>
                </div>
              </div>

              <!-- Item Name -->
              <div class="col-lg-4 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Item Name <span class="required-star">*</span></label>
                  <b-form-input
                    type="text"
                    v-model="form.itemname"
                    ref="itemNameInput"
                    placeholder="e.g. Tata Tea Premium 500g"
                    :class="{ 'is-invalid': errors.itemname }"
                    @keypress.enter="focusNext('itemcodeInput')"
                  />
                  <div class="invalid-feedback" v-if="errors.itemname">{{ errors.itemname }}</div>
                </div>
              </div>

              <!-- Item Code -->
              <div class="col-lg-2 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Item Code <span class="required-star">*</span></label>
                  <b-form-input
                    type="text"
                    v-model="form.itemcode"
                    ref="itemcodeInput"
                    placeholder="e.g. IT-0001"
                    :class="{ 'is-invalid': errors.itemcode }"
                    @input="onItemCodeInput"
                    @blur="checkItemCodeAvailability"
                    @keypress.enter="focusNext('barcodeInput')"
                  />
                  <div v-if="itemCodeLookup.status" class="field-status" :class="itemCodeLookup.status">
                    {{ itemCodeLookup.message }}
                  </div>
                  <div class="invalid-feedback" v-if="errors.itemcode">{{ errors.itemcode }}</div>
                </div>
              </div>

              <!-- Barcode -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Barcode <span class="optional-tag">Optional</span></label>
                  <div class="input-with-icon">
                    <i class="fas fa-barcode input-icon"></i>
                    <b-form-input
                      type="text"
                      v-model="form.barcode"
                      ref="barcodeInput"
                      placeholder="Scan or enter barcode"
                    />
                  </div>
                </div>
              </div>

              <!-- Rack No -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Rack No <span class="optional-tag">Optional</span></label>
                  <b-form-input
                    type="text"
                    v-model="form.rackno"
                    placeholder="e.g. A1, Rack-02"
                  />
                </div>
              </div>

              <!-- Item Description -->
              <div class="col-lg-9">
                <div class="form-group">
                  <label class="form-label">Description <span class="optional-tag">Optional</span></label>
                  <b-form-textarea
                    v-model="form.itemdescription"
                    placeholder="Brief description of the item..."
                    rows="2"
                    max-rows="4"
                  />
                </div>
              </div>

            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 2 · PRICING
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-rupee-sign"></i></div>
              <div>
                <h6 class="section-title">Pricing</h6>
                <p class="section-sub">Purchase, selling and wholesale rates</p>
              </div>
            </div>

            <div class="row g-3 mb-4">

              <!-- Purchase Rate -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Purchase Rate <span class="optional-tag">Optional</span></label>
                  <div class="input-currency">
                    <span class="currency-symbol">₹</span>
                    <b-form-input
                      type="number" min="0" step="0.01"
                      v-model="form.purchaserate"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <!-- MRP -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">MRP <span class="optional-tag">Optional</span></label>
                  <div class="input-currency">
                    <span class="currency-symbol">₹</span>
                    <b-form-input type="number" min="0" step="0.01" v-model="form.mrp" placeholder="0.00" />
                  </div>
                </div>
              </div>

              <!-- Selling Price -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Selling Price <span class="required-star">*</span></label>
                  <div class="input-currency">
                    <span class="currency-symbol">₹</span>
                    <b-form-input
                      type="number" min="0" step="0.01"
                      v-model="form.unitprice"
                      placeholder="0.00"
                      :class="{ 'is-invalid': errors.unitprice }"
                    />
                  </div>
                  <div class="invalid-feedback" v-if="errors.unitprice">{{ errors.unitprice }}</div>
                </div>
              </div>

              <!-- Wholesale Rate -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Wholesale Rate <span class="optional-tag">Optional</span></label>
                  <div class="input-currency">
                    <span class="currency-symbol">₹</span>
                    <b-form-input type="number" min="0" step="0.01" v-model="form.wholesalerate" placeholder="0.00" />
                  </div>
                </div>
              </div>

            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 3 · STOCK & UNIT
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-warehouse"></i></div>
              <div>
                <h6 class="section-title">Stock & Unit</h6>
                <p class="section-sub">Inventory levels and measurement unit</p>
              </div>
            </div>

            <div class="row g-3 mb-4">

              <!-- Unit -->
              <div class="col-lg-2 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Unit <span class="required-star">*</span></label>
                  <div class="unit-field-row">
                    <button
                      type="button"
                      class="unit-add-btn"
                      title="Add new unit"
                      @click="openUnitModal"
                    >
                      <i class="fas fa-plus"></i>
                    </button>
                    <model-list-select
                      :list="unitListFromApi"
                      v-model="form.unit"
                      option-value="unitname"
                      option-text="unitname"
                      placeholder="Select unit"
                      :class="{ 'is-invalid': errors.unit }"
                    />
                  </div>
                  <div class="invalid-feedback" v-if="errors.unit">{{ errors.unit }}</div>
                </div>
              </div>

              <!-- Available Stock -->
              <div class="col-lg-2 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Available Stock <span class="required-star">*</span></label>
                  <b-form-input
                    type="number" min="0" step="0.01"
                    v-model="form.available"
                    placeholder="0"
                    :class="{ 'is-invalid': errors.available }"
                  />
                  <div class="invalid-feedback" v-if="errors.available">{{ errors.available }}</div>
                </div>
              </div>

              <!-- Min Stock -->
              <div class="col-lg-2 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Min Stock <span class="required-star">*</span></label>
                  <b-form-input type="number" min="0" v-model="form.minstock" placeholder="e.g. 5" :class="{ 'is-invalid': errors.minstock }" />
                  <small class="form-hint">Low stock alert will use this value</small>
                  <div class="invalid-feedback" v-if="errors.minstock">{{ errors.minstock }}</div>
                </div>
              </div>

            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 4 · TAX DETAILS
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-percent"></i></div>
              <div>
                <h6 class="section-title">Tax Details</h6>
                <p class="section-sub">GST and HSN classification with comprehensive tax breakdown</p>
              </div>
            </div>

            <div class="tax-panel mb-4">

              <!-- HSN Code -->
              <div class="row g-3 mb-3">
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">{{ form.taxtype === 'SAC' ? 'SAC Code' : 'HSN Code' }} <span v-if="isTaxableType && !form.is_manual_tax_rate" class="required-star">*</span></label>
                    <b-form-input type="text" v-model.trim="form.hsncode" :disabled="form.is_manual_tax_rate || !isTaxableType" placeholder="e.g. 09024090" :class="{ 'is-invalid': errors.hsncode }" @blur="resolveTaxRateForCode" @keyup.enter="resolveTaxRateForCode" />
                    <small class="form-hint">Enter a code from HSN Tax Master to auto-fill the tax rate</small>
                    <div v-if="taxRateLookup.status" class="tax-rate-status" :class="taxRateLookup.status">
                      {{ taxRateLookup.message }}
                    </div>
                    <button
                      v-if="taxRateLookup.status === 'missing'"
                      type="button"
                      class="inline-action-btn mt-2"
                      @click="openTaxRateModal"
                    >
                      <i class="fas fa-plus"></i>
                      Add {{ form.taxtype === 'SAC' ? 'SAC' : 'HSN' }} Rate
                    </button>
                    <div class="invalid-feedback" v-if="errors.hsncode">{{ errors.hsncode }}</div>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">Tax Type <span class="required-star">*</span></label>
                    <b-form-select 
                      v-model="form.taxtype" 
                      :options="taxTypeOptions"
                      @change="handleTaxTypeChange"
                    />
                    <small class="form-hint">Nature of tax applicable to this item</small>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">Tax Inclusive?</label>
                    <div class="form-check form-switch mt-2">
                      <b-form-checkbox 
                        v-model="form.istaxinclusive"
                        switch
                      >
                        Price includes tax
                      </b-form-checkbox>
                    </div>
                    <small class="form-hint">MRP-based pricing (tax included) vs Base price (tax added)</small>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12" v-if="isTaxableType">
                  <div class="form-group mb-0">
                    <label class="form-label">Manual Tax Rate?</label>
                    <b-form-checkbox v-model="form.is_manual_tax_rate" switch @change="handleManualTaxToggle">
                      Enter rate manually
                    </b-form-checkbox>
                    <small class="form-hint">Use only when the code is not in Tax Rates</small>
                  </div>
                </div>
              </div>

              <!-- GST Rate Selector -->
              <div class="row g-0" v-if="isTaxableType && form.is_manual_tax_rate">
                <div class="col-12">
                  <label class="form-label mb-2">Tax Rate <span class="required-star">*</span></label>
                  <div class="gst-rate-selector">
                    <button
                      v-for="rate in gstRates"
                      :key="rate.label"
                      type="button"
                      class="gst-rate-btn"
                      :class="{ active: selectedGstRate === rate.label }"
                      @click="applyGstRate(rate)"
                    >
                      <span class="rate-label">{{ rate.label }}</span>
                      <span class="rate-breakdown" v-if="rate.total > 0">CGST {{ rate.cgst }}% + SGST {{ rate.sgst }}% + IGST {{ rate.total }}%</span>
                      <span class="rate-breakdown" v-else>Nil rated</span>
                    </button>
                    <button
                      type="button"
                      class="gst-rate-btn"
                      :class="{ active: selectedGstRate === 'custom' }"
                      @click="selectedGstRate = 'custom'"
                    >
                      <span class="rate-label">Custom</span>
                      <span class="rate-breakdown">Enter manually</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Live GST Breakdown -->
              <div class="gst-breakdown-row mt-3" v-if="isTaxableType && form.is_manual_tax_rate">
                <div class="gst-field">
                  <label class="form-label">Total Tax %</label>
                  <div class="input-with-suffix">
                    <b-form-input
                      type="number" min="0" max="100" step="0.01"
                      v-model="form.taxpercent"
                      placeholder="0.00"
                      @input="syncTaxFromTotal"
                      :readonly="selectedGstRate !== 'custom'"
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
                <div class="gst-divider-arrow">
                  <i class="fas fa-arrow-right"></i>
                  <span>Split equally</span>
                </div>
                <div class="gst-field">
                  <label class="form-label">CGST</label>
                  <div class="input-with-suffix">
                    <b-form-input
                      type="number" min="0" max="50" step="0.01"
                      v-model="form.cgstrate"
                      placeholder="0.00"
                      @input="syncTaxFromCgst"
                      :readonly="selectedGstRate !== 'custom'"
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
                <div class="gst-plus">+</div>
                <div class="gst-field">
                  <label class="form-label">SGST</label>
                  <div class="input-with-suffix">
                    <b-form-input
                      type="number" min="0" max="50" step="0.01"
                      v-model="form.sgstrate"
                      placeholder="0.00"
                      @input="syncTaxFromSgst"
                      :readonly="selectedGstRate !== 'custom'"
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
                <div class="gst-plus">+</div>
                <div class="gst-field">
                  <label class="form-label">IGST</label>
                  <div class="input-with-suffix">
                    <b-form-input
                      type="number" min="0" max="100" step="0.01"
                      v-model="form.igstrate"
                      placeholder="0.00"
                      @input="syncTaxFromIgst"
                      :readonly="selectedGstRate !== 'custom'"
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
                <!-- Live Price Preview -->
                <div class="gst-preview" v-if="form.unitprice > 0 && form.taxpercent > 0">
                  <div class="preview-label">
                    {{ form.istaxinclusive ? 'Tax Component' : 'Price + Tax' }}
                  </div>
                  <div class="preview-value">₹{{ priceWithTax }}</div>
                  <div class="preview-hint" v-if="form.istaxinclusive">
                    Customer pays ₹{{ form.unitprice }} (tax included)
                  </div>
                </div>
              </div>

              <!-- Tax Type Specific Messages -->
              <div class="tax-type-message mt-3" v-if="form.taxtype !== 'GST'">
                <div class="alert alert-info">
                  <i class="fas fa-info-circle"></i>
                  <span v-if="form.taxtype === 'EXEMPT'">
                    This item is tax-exempt under GST. No tax will be charged.
                  </span>
                  <span v-else-if="form.taxtype === 'SAC'">
                    This is a service item (SAC code). GST will be applied as per service tax rules.
                  </span>
                  <span v-else-if="form.taxtype === 'NONGST'">
                    This item is outside GST scope (e.g., alcohol, petroleum). No GST will be applied.
                  </span>
                </div>
              </div>

            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 5 · UNIT OF MEASUREMENT
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-ruler-combined"></i></div>
              <div>
                <h6 class="section-title">Unit of Measurement (UOM)</h6>
                <p class="section-sub">Define how this item is packed and measured</p>
              </div>
            </div>

            <div class="uom-panel mb-4">
              <div class="row g-3 align-items-end">

                <!-- Upper Unit Name -->
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">
                      <span class="uom-badge-inline upper">UPPER UNIT</span>
                      Unit Name <span class="optional-tag">Optional</span>
                    </label>
                    <b-form-input
                      type="text"
                      v-model="form.uom_upper"
                      placeholder="e.g. Box, Carton, Case"
                    />
                  </div>
                </div>

                <!-- Conversion Factor — inline chip style like screenshot -->
                <div class="col-lg-4 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">
                      <i class="fas fa-exchange-alt me-1" style="color:#3d5af1;"></i>
                      Conversion Factor
                    </label>
                    <div class="uom-conversion-row">
                      <span class="uom-chip upper">{{ form.uom_upper || 'Upper' }}</span>
                      <span class="uom-equals">=</span>
                      <b-form-input
                        type="number"
                        min="1"
                        step="1"
                        v-model="form.conversionfactor"
                        placeholder="1"
                        class="uom-factor-input"
                      />
                      <span class="uom-chip lower">{{ form.uom_lower || 'Lower' }}</span>
                    </div>
                    <small class="form-hint">How many lower units fit in one upper unit</small>
                  </div>
                </div>

                <!-- Lower Unit Name -->
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">
                      <span class="uom-badge-inline lower">LOWER UNIT</span>
                      Unit Name <span class="optional-tag">Optional</span>
                    </label>
                    <b-form-input
                      type="text"
                      v-model="form.uom_lower"
                      placeholder="e.g. Piece, Packet, Bottle"
                    />
                  </div>
                </div>

                <!-- Live Preview -->
                <div class="col-lg-2 col-sm-12 col-12" v-if="form.uom_upper && form.uom_lower && form.conversionfactor">
                  <div class="uom-example-card">
                    <i class="fas fa-lightbulb me-1"></i>
                    1 {{ form.uom_upper }} = {{ form.conversionfactor }} {{ form.uom_lower }}
                  </div>
                </div>

              </div>
            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 6 · TRACKING OPTIONS
                 ⚠️  FEATURE DISABLED — Batch & Expiry
                 tracking will be implemented in a
                 future release. Uncomment this section
                 and the corresponding form fields
                 (batchenabled, expiryenabled) in
                 data() to re-enable.
            ══════════════════════════════════════

            <div class="section-header">
              <div class="section-icon"><i class="fas fa-sliders-h"></i></div>
              <div>
                <h6 class="section-title">Tracking Options</h6>
                <p class="section-sub">Enable additional tracking for this item</p>
              </div>
            </div>

            <div class="row g-3 mb-4">
              <div class="col-lg-4 col-sm-6 col-12">
                <div
                  class="toggle-option-card"
                  :class="{ active: form.batchenabled }"
                  @click="form.batchenabled = !form.batchenabled"
                >
                  <div class="toggle-icon"><i class="fas fa-layer-group"></i></div>
                  <div class="toggle-content">
                    <div class="toggle-title">Batch Tracking</div>
                    <div class="toggle-desc">Track stock by batch/lot number</div>
                  </div>
                  <div class="toggle-switch-wrap">
                    <div class="toggle-pill" :class="{ on: form.batchenabled }">
                      <div class="toggle-thumb"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 col-sm-6 col-12">
                <div
                  class="toggle-option-card"
                  :class="{ active: form.expiryenabled }"
                  @click="form.expiryenabled = !form.expiryenabled"
                >
                  <div class="toggle-icon"><i class="fas fa-calendar-times"></i></div>
                  <div class="toggle-content">
                    <div class="toggle-title">Expiry Tracking</div>
                    <div class="toggle-desc">Monitor expiry dates for batches</div>
                  </div>
                  <div class="toggle-switch-wrap">
                    <div class="toggle-pill" :class="{ on: form.expiryenabled }">
                      <div class="toggle-thumb"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section-divider"></div>
            -->

            <!-- ══════════════════════════════════════
                 SECTION 7 · ITEM IMAGES
                 ⚠️  FEATURE DISABLED — Image upload
                 will be enabled in a future release.
                 Uncomment this section and the
                 localImages / maxImages / uploadError
                 data fields + image methods to
                 re-enable.
            ══════════════════════════════════════

            <div class="section-header">
              <div class="section-icon"><i class="fas fa-images"></i></div>
              <div>
                <h6 class="section-title">Item Images</h6>
                <p class="section-sub">Optional — up to 4 images</p>
              </div>
            </div>

            <div class="mb-4">
              <input
                type="file"
                accept="image/*"
                multiple
                @change="handleImageUpload"
                ref="imageUploader"
                style="display: none;"
              />
              <div class="image-grid">
                <div v-for="(image, index) in localImages" :key="index" class="image-tile">
                  <img :src="image.url" alt="Item Image" />
                  <button type="button" class="image-remove" @click="removeLocalImage(index)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div
                  class="image-add-tile"
                  @click="$refs.imageUploader.click()"
                  v-if="localImages.length < maxImages"
                >
                  <i class="fas fa-plus"></i>
                  <span>Add Photo</span>
                </div>
              </div>
              <small class="text-danger d-block mt-1" v-if="uploadError">{{ uploadError }}</small>
              <small class="form-hint" v-else>JPG, PNG, WEBP supported · Max 4 images</small>
            </div>

            <div class="section-divider"></div>
            -->

            <!-- ══════════════════════════════════════
                 ACTION BUTTONS
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-images"></i></div>
              <div>
                <h6 class="section-title">Item Images</h6>
                <p class="section-sub">Optional product photos</p>
              </div>
            </div>

            <div class="mb-4">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                multiple
                @change="handleImageUpload"
                ref="imageUploader"
                style="display: none;"
              />
              <div class="image-grid">
                <div v-for="(image, index) in localImages" :key="image.url" class="image-tile">
                  <img :src="image.url" alt="Item Image" />
                  <button type="button" class="image-remove" @click="removeLocalImage(index)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div
                  class="image-add-tile"
                  @click="$refs.imageUploader.click()"
                  v-if="localImages.length < maxImages"
                >
                  <i class="fas fa-plus"></i>
                  <span>Add Photo</span>
                </div>
              </div>
              <small class="text-danger d-block mt-1" v-if="uploadError">{{ uploadError }}</small>
              <small class="form-hint" v-else>JPG, PNG, WEBP supported. Max 2 MB per image.</small>
            </div>

            <div class="section-divider"></div>

            <div class="form-actions">
              <button type="button" class="btn btn-submit" @click="postData()">
                <i class="fas fa-check me-2"></i>Save Item
              </button>
              <router-link to="productlist" class="btn btn-cancel">
                <i class="fas fa-times me-2"></i>Cancel
              </router-link>
            </div>

          </div><!-- /card-body -->
        </div><!-- /card -->
      </div>
    </div>

    <div
      v-if="showUnitModal"
      class="quick-create-modal-backdrop"
      @click.self="closeUnitModal"
    >
      <div class="quick-create-modal" role="dialog" aria-modal="true" aria-labelledby="unit-quick-create-title">
        <div class="quick-create-modal-header">
          <div>
            <h5 id="unit-quick-create-title" class="quick-create-modal-title">Add New Unit</h5>
            <p class="quick-create-modal-subtitle">The unit will be available immediately in this form.</p>
          </div>
          <button type="button" class="quick-create-close" @click="closeUnitModal" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Unit Name <span class="required-star">*</span></label>
            <b-form-input
              ref="unitModalNameInput"
              v-model="unitModalForm.unitname"
              type="text"
              placeholder="e.g. Nos, Kg, Box"
              @keyup.enter="createUnitFromModal"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Unit Code</label>
            <b-form-input
              v-model="unitModalForm.unitcode"
              type="text"
              placeholder="Optional short code"
              @keyup.enter="createUnitFromModal"
            />
          </div>
          <div class="col-12">
            <label class="form-label">Description</label>
            <b-form-textarea
              v-model="unitModalForm.description"
              rows="2"
              placeholder="Optional description"
            />
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-light" @click="closeUnitModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="unitModalSaving" @click="createUnitFromModal">
            {{ unitModalSaving ? "Saving..." : "Save Unit" }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showTaxRateModal"
      class="quick-create-modal-backdrop"
      @click.self="closeTaxRateModal"
    >
      <div class="quick-create-modal" role="dialog" aria-modal="true" aria-labelledby="tax-rate-quick-create-title">
        <div class="quick-create-modal-header">
          <div>
            <h5 id="tax-rate-quick-create-title" class="quick-create-modal-title">Add {{ taxRateModalForm.code_type }} Rate</h5>
            <p class="quick-create-modal-subtitle">This code will be available immediately for the item.</p>
          </div>
          <button type="button" class="quick-create-close" @click="closeTaxRateModal" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">Code Type</label>
            <b-form-input v-model="taxRateModalForm.code_type" type="text" readonly />
          </div>
          <div class="col-md-8">
            <label class="form-label">Code</label>
            <b-form-input v-model="taxRateModalForm.code" type="text" readonly />
          </div>
          <div class="col-md-6">
            <label class="form-label">Tax Percent <span class="required-star">*</span></label>
            <b-form-input
              ref="taxRatePercentInput"
              v-model.number="taxRateModalForm.tax_percent"
              type="number"
              min="0"
              max="100"
              step="0.01"
              placeholder="e.g. 18"
              @keyup.enter="createTaxRateFromModal"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Effective From <span class="required-star">*</span></label>
            <b-form-input
              v-model="taxRateModalForm.effective_from"
              type="date"
              @keyup.enter="createTaxRateFromModal"
            />
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-light" @click="closeTaxRateModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="taxRateModalSaving" @click="createTaxRateFromModal">
            {{ taxRateModalSaving ? "Saving..." : "Save Tax Rate" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ModelListSelect } from "vue-search-select";
import axios from "axios";

async function ImageUploadApi(item_id, files) {
  const formData = new FormData();
  files.forEach((file) => formData.append("image_files", file));
  const response = await axios.post(`/v1/items/${item_id}/images`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

export default {
  name: "addproduct",
  components: { ModelListSelect },

  data() {
    return {
      title: "Add Item",
      title1: "Create a new inventory item",

      form: {
        // Basic
        itemname: "",
        itemdescription: "",
        itemcategory: "",
        itemcode: "",
        barcode: "",
        rackno: "",

        // Pricing
        unitprice: "",
        purchaserate: "",
        mrp: "",
        wholesalerate: "",

        // Stock
        unit: "",
        available: "",
        threshold: "",
        minstock: "",
        maxstock: "",

        // Tax
        hsncode: "",
        taxpercent: "",
        cgstrate: "",
        sgstrate: "",
        igstrate: "",
        is_manual_tax_rate: false,
        taxtype: "GST",
        istaxinclusive: true,

        // ── TRACKING OPTIONS (disabled — future feature) ──
        // batchenabled: false,
        // expiryenabled: false,

        // UOM
        uom_upper: "",
        uom_lower: "",
        conversionfactor: "",
      },

      errors: {},

      categoryListFromApi: [],
      unitListFromApi: [],
      showUnitModal: false,
      unitModalSaving: false,
      unitModalForm: {
        unitname: "",
        unitcode: "",
        description: "",
      },
      itemCodeLookup: {
        status: "",
        message: "",
      },
      itemCodeCheckTimer: null,
      itemCodeChecking: false,
      showTaxRateModal: false,
      taxRateModalSaving: false,
      taxRateModalForm: {
        code_type: "HSN",
        code: "",
        tax_percent: "",
        effective_from: "",
      },

      // Tax type options
      taxTypeOptions: [
        { value: "GST", text: "GST (Goods)" },
        { value: "SAC", text: "SAC (Services)" },
        { value: "EXEMPT", text: "Exempt" },
        { value: "NONGST", text: "Non-GST" }
      ],

      // GST quick-select rates (standard Indian GST slabs)
      gstRates: [
        { label: "0%",  total: 0,  cgst: 0,   sgst: 0,   igst: 0   },
        { label: "5%",  total: 5,  cgst: 2.5, sgst: 2.5, igst: 5   },
        { label: "12%", total: 12, cgst: 6,   sgst: 6,   igst: 12  },
        { label: "18%", total: 18, cgst: 9,   sgst: 9,   igst: 18  },
        { label: "28%", total: 28, cgst: 14,  sgst: 14,  igst: 28  },
      ],
      selectedGstRate: null,
      taxRateLookup: {
        status: "",
        message: "",
      },
      localImages: [],
      maxImages: 20,
      uploadError: "",
      collapsibleSectionCleanups: [],

      // ── IMAGE UPLOAD (disabled — future feature) ──
      // localImages: [],
      // maxImages: 4,
      // uploadError: "",
    };
  },

  computed: {
    isTaxableType() {
      return ["GST", "SAC"].includes(this.form.taxtype);
    },
    priceWithTax() {
      const price = parseFloat(this.form.unitprice) || 0;
      const tax   = parseFloat(this.form.taxpercent) || 0;
      
      if (this.form.istaxinclusive) {
        // Tax is included in price, show tax component
        const taxAmount = (price * tax) / (100 + tax);
        return taxAmount.toFixed(2);
      } else {
        // Tax is added to price
        return (price * tax / 100).toFixed(2);
      }
    },
  },

  created() {
    this.fetchCategoryListFromApi();
    this.fetchUnitListFromApi();
  },

  mounted() {
    this.$refs.itemNameInput.focus();
    this.setupCollapsibleSections();
  },

  methods: {
    setupCollapsibleSections() {
      this.$nextTick(() => {
        const cardBody = this.$el.querySelector(".card-body");
        if (!cardBody) return;

        Array.from(cardBody.children)
          .filter((element) => element.classList?.contains("section-header"))
          .forEach((header) => {
            if (header.dataset.collapsibleReady === "true") return;
            header.dataset.collapsibleReady = "true";
            header.classList.add("collapsible-section-header");
            header.setAttribute("role", "button");
            header.setAttribute("tabindex", "0");
            header.setAttribute("aria-expanded", "true");

            const toggleButton = document.createElement("button");
            toggleButton.type = "button";
            toggleButton.className = "section-toggle-btn";
            toggleButton.setAttribute("aria-label", "Toggle section");
            toggleButton.innerHTML = '<i class="fas fa-chevron-down"></i>';
            header.appendChild(toggleButton);

            const toggleSection = () => {
              const isCollapsed = header.classList.toggle("is-collapsed");
              header.setAttribute("aria-expanded", String(!isCollapsed));
              let sibling = header.nextElementSibling;
              while (sibling && !sibling.classList?.contains("section-divider")) {
                sibling.style.display = isCollapsed ? "none" : "";
                sibling = sibling.nextElementSibling;
              }
            };

            const onClick = () => toggleSection();
            const onKeydown = (event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              toggleSection();
            };

            header.addEventListener("click", onClick);
            header.addEventListener("keydown", onKeydown);
            this.collapsibleSectionCleanups.push(() => {
              header.removeEventListener("click", onClick);
              header.removeEventListener("keydown", onKeydown);
            });
          });
      });
    },

    // ── Focus helper ───────────────────────────────────
    focusNext(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el && el.$el) el.$el.querySelector("input")?.focus();
        else if (el) el.focus?.();
      });
    },

    // ── Category fetch ─────────────────────────────────
    todayString() {
      const date = new Date();
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },

    clearItemCodeLookup() {
      this.itemCodeLookup = { status: "", message: "" };
    },

    onItemCodeInput() {
      if (this.errors.itemcode && this.form.itemcode.trim()) this.errors.itemcode = "";
      clearTimeout(this.itemCodeCheckTimer);
      const code = this.form.itemcode.trim();
      if (!code) {
        this.clearItemCodeLookup();
        return;
      }
      this.itemCodeCheckTimer = setTimeout(() => {
        this.checkItemCodeAvailability();
      }, 450);
    },

    async checkItemCodeAvailability() {
      const code = this.form.itemcode.trim();
      if (!code) {
        this.clearItemCodeLookup();
        return false;
      }

      this.itemCodeChecking = true;
      this.itemCodeLookup = { status: "loading", message: "Checking item code..." };
      try {
        const response = await axios.get("/v1/items/availability/item-code", {
          params: { itemcode: code },
        });
        if (response.data?.exists) {
          const itemName = response.data.itemname ? ` (${response.data.itemname})` : "";
          this.errors.itemcode = `Item code already exists${itemName}.`;
          this.itemCodeLookup = {
            status: "missing",
            message: `Item code "${code}" is already used${itemName}.`,
          };
          return false;
        }
        if (this.errors.itemcode?.startsWith("Item code already exists")) {
          this.errors.itemcode = "";
        }
        this.itemCodeLookup = {
          status: "found",
          message: `Item code "${code}" is available.`,
        };
        return true;
      } catch (error) {
        console.error(error);
        this.itemCodeLookup = {
          status: "missing",
          message: "Could not verify item code right now.",
        };
        return true;
      } finally {
        this.itemCodeChecking = false;
      }
    },

    async fetchCategoryListFromApi() {
      try {
        const response = await axios.get("/v1/category");
        this.categoryListFromApi = response.data;
      } catch (error) {
        console.error("Error fetching category list:", error);
      }
    },

    async fetchUnitListFromApi() {
      try {
        const response = await axios.get("/v1/units");
        this.unitListFromApi = response.data;
      } catch (error) {
        console.error("Error fetching unit list:", error);
      }
    },

    getEmptyUnitModalForm() {
      return {
        unitname: "",
        unitcode: "",
        description: "",
      };
    },

    openUnitModal() {
      this.unitModalForm = this.getEmptyUnitModalForm();
      this.showUnitModal = true;
      this.$nextTick(() => {
        const input = this.$refs.unitModalNameInput;
        input?.focus?.();
      });
    },

    closeUnitModal() {
      this.showUnitModal = false;
      this.unitModalSaving = false;
      this.unitModalForm = this.getEmptyUnitModalForm();
    },

    async createUnitFromModal() {
      const unitname = this.unitModalForm.unitname?.trim();
      if (!unitname) {
        Swal.fire({ icon: "warning", title: "Unit name required", text: "Please enter a unit name." });
        return;
      }

      this.unitModalSaving = true;
      try {
        const payload = {
          unitname,
          unitcode: this.unitModalForm.unitcode?.trim() || null,
          description: this.unitModalForm.description?.trim() || null,
        };
        const response = await axios.post("/v1/units", payload);
        await this.fetchUnitListFromApi();
        this.form.unit = response.data?.unitname || unitname;
        this.errors.unit = "";
        this.closeUnitModal();
        Swal.fire({
          icon: "success",
          title: "Unit added",
          text: `"${unitname}" is ready to use.`,
          timer: 1400,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Could not add unit",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.unitModalSaving = false;
      }
    },

    // ── Tax type change handler ───────────────────────
    openTaxRateModal() {
      const code = String(this.form.hsncode || "").trim();
      if (!code) {
        this.errors.hsncode = `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} code is required.`;
        return;
      }
      this.taxRateModalForm = {
        code_type: this.form.taxtype === "SAC" ? "SAC" : "HSN",
        code,
        tax_percent: "",
        effective_from: this.todayString(),
      };
      this.showTaxRateModal = true;
      this.$nextTick(() => {
        const input = this.$refs.taxRatePercentInput;
        if (input && input.$el) input.$el.querySelector("input")?.focus();
        else input?.focus?.();
      });
    },

    closeTaxRateModal() {
      this.showTaxRateModal = false;
      this.taxRateModalSaving = false;
      this.taxRateModalForm = {
        code_type: this.form.taxtype === "SAC" ? "SAC" : "HSN",
        code: String(this.form.hsncode || "").trim(),
        tax_percent: "",
        effective_from: this.todayString(),
      };
    },

    async createTaxRateFromModal() {
      const taxPercent = Number(this.taxRateModalForm.tax_percent);
      if (Number.isNaN(taxPercent) || taxPercent < 0 || taxPercent > 100) {
        Swal.fire({ icon: "warning", title: "Invalid tax percent", text: "Enter a tax percent between 0 and 100." });
        return;
      }
      if (!this.taxRateModalForm.effective_from) {
        Swal.fire({ icon: "warning", title: "Effective date required", text: "Please select an effective from date." });
        return;
      }

      this.taxRateModalSaving = true;
      try {
        await axios.post("/v1/tax/rates", {
          code_type: this.taxRateModalForm.code_type,
          code: this.taxRateModalForm.code,
          tax_percent: taxPercent,
          effective_from: this.taxRateModalForm.effective_from,
          effective_to: null,
        });
        this.closeTaxRateModal();
        await this.resolveTaxRateForCode();
        Swal.fire({
          icon: "success",
          title: "Tax rate added",
          text: `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} ${this.form.hsncode} is ready to use.`,
          timer: 1400,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Could not add tax rate",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.taxRateModalSaving = false;
      }
    },

    handleTaxTypeChange() {
      if (!this.isTaxableType) {
        // Reset tax fields for non-GST items
        this.form.hsncode = "";
        this.form.is_manual_tax_rate = false;
        this.form.taxpercent = "0";
        this.form.cgstrate = "0";
        this.form.sgstrate = "0";
        this.form.igstrate = "0";
        this.selectedGstRate = null;
        this.clearTaxRateLookup();
      } else if (this.form.hsncode && !this.form.is_manual_tax_rate) {
        this.resolveTaxRateForCode();
      }
    },
    handleManualTaxToggle() {
      if (this.form.is_manual_tax_rate) {
        this.form.hsncode = "";
        this.selectedGstRate = "custom";
        this.clearTaxRateLookup();
      } else {
        this.form.taxpercent = "";
        this.form.cgstrate = "";
        this.form.sgstrate = "";
        this.form.igstrate = "";
        this.selectedGstRate = null;
      }
    },

    clearTaxRateLookup() {
      this.taxRateLookup = { status: "", message: "" };
    },

    async resolveTaxRateForCode() {
      if (!this.isTaxableType || this.form.is_manual_tax_rate) return;
      const code = String(this.form.hsncode || "").trim();
      if (!code) {
        this.clearTaxRateLookup();
        return;
      }

      this.taxRateLookup = { status: "loading", message: "Checking HSN Tax Master..." };
      try {
        const response = await axios.get("/v1/tax/rates/resolve", {
          params: {
            code_type: this.form.taxtype === "SAC" ? "SAC" : "HSN",
            code,
          },
        });
        const rate = response.data || {};
        this.form.taxpercent = String(rate.tax_percent ?? "");
        this.form.cgstrate = String(rate.cgstrate ?? "");
        this.form.sgstrate = String(rate.sgstrate ?? "");
        this.form.igstrate = String(rate.igstrate ?? "");
        const match = this.gstRates.find((slab) => Number(slab.total) === Number(rate.tax_percent));
        this.selectedGstRate = match ? match.label : "custom";
        this.taxRateLookup = {
          status: "found",
          message: `${rate.code_type} ${rate.code} is active at ${this.form.taxpercent}% tax.`,
        };
        this.errors.hsncode = "";
      } catch (error) {
        this.form.taxpercent = "";
        this.form.cgstrate = "";
        this.form.sgstrate = "";
        this.form.igstrate = "";
        this.selectedGstRate = null;
        this.taxRateLookup = {
          status: "missing",
          message: error.response?.data?.detail || "No active tax rate found. Add this code in HSN Tax Master first.",
        };
      }
    },

    // ── GST rate quick-apply ───────────────────────────
    applyGstRate(rate) {
      this.selectedGstRate  = rate.label;
      this.form.taxpercent  = rate.total > 0 ? String(rate.total) : "0";
      this.form.cgstrate    = String(rate.cgst);
      this.form.sgstrate    = String(rate.sgst);
      this.form.igstrate    = String(rate.igst);
    },

    // ── Tax field sync ─────────────────────────────────
    syncTaxFromTotal() {
      const total    = parseFloat(this.form.taxpercent) || 0;
      const half     = parseFloat((total / 2).toFixed(2));
      this.form.cgstrate = String(half);
      this.form.sgstrate = String(half);
      this.form.igstrate = String(total);
    },
    syncTaxFromCgst() {
      const cgst           = parseFloat(this.form.cgstrate) || 0;
      const sgst           = parseFloat(this.form.sgstrate) || 0;
      this.form.taxpercent = String(parseFloat((cgst + sgst).toFixed(2)));
      this.form.igstrate = this.form.taxpercent;
    },
    syncTaxFromSgst() {
      const cgst           = parseFloat(this.form.cgstrate) || 0;
      const sgst           = parseFloat(this.form.sgstrate) || 0;
      this.form.taxpercent = String(parseFloat((cgst + sgst).toFixed(2)));
      this.form.igstrate = this.form.taxpercent;
    },
    syncTaxFromIgst() {
      const igst = parseFloat(this.form.igstrate) || 0;
      this.form.taxpercent = String(igst);
      const half = parseFloat((igst / 2).toFixed(2));
      this.form.cgstrate = String(half);
      this.form.sgstrate = String(half);
    },

    // ── IMAGE METHODS ─────────────────────────────────
    handleImageUpload(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;

      const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      const maxSizeBytes = 2 * 1024 * 1024;
      const combinedCount = this.localImages.length + files.length;
      if (combinedCount > this.maxImages) {
        this.uploadError = `You can only upload up to ${this.maxImages} images.`;
        return;
      }

      for (const file of files) {
        if (!allowedTypes.includes(file.type)) {
          this.uploadError = "Only JPG, JPEG, PNG and WEBP images are allowed.";
          return;
        }
        if (file.size > maxSizeBytes) {
          this.uploadError = "Each image must be 2 MB or smaller.";
          return;
        }
      }

      files.forEach((file) => {
        const url = URL.createObjectURL(file);
        this.localImages.push({ file, url, isNew: true });
      });
      this.uploadError = "";
      event.target.value = null;
    },

    removeLocalImage(index) {
      const image = this.localImages[index];
      if (image?.url && image.isNew) {
        URL.revokeObjectURL(image.url);
      }
      this.localImages.splice(index, 1);
      this.uploadError = "";
    },

    async uploadImages(itemId) {
      const files = this.localImages.filter((img) => img.isNew).map((img) => img.file);
      if (!files.length) return [];
      return await ImageUploadApi(itemId, files);
    },

    // ── Validation ─────────────────────────────────────
    validate() {
      this.errors = {};
      if (!this.form.itemname.trim())
        this.errors.itemname      = "Item name is required.";
      if (!this.form.itemcode.trim())
        this.errors.itemcode      = "Item code is required.";
      if (!this.form.itemcategory)
        this.errors.itemcategory  = "Please select a category.";
      if (!this.form.unit.trim())
        this.errors.unit          = "Unit is required.";
      if (this.form.unitprice === "" || isNaN(this.form.unitprice) || Number(this.form.unitprice) < 0)
        this.errors.unitprice     = "Enter a valid unit price.";
      if (this.form.available === "" || isNaN(this.form.available) || Number(this.form.available) < 0)
        this.errors.available     = "Enter a valid available quantity.";
      if (this.form.minstock === "" || isNaN(this.form.minstock) || Number(this.form.minstock) < 0)
        this.errors.minstock      = "Enter a valid minimum stock value.";
      if (this.isTaxableType && !this.form.is_manual_tax_rate && !String(this.form.hsncode || "").trim())
        this.errors.hsncode       = `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} code is required unless manual tax rate is enabled.`;
      if (this.isTaxableType && !this.form.is_manual_tax_rate && String(this.form.hsncode || "").trim() && !String(this.form.taxpercent || "").trim())
        this.errors.hsncode       = `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} code must exist in HSN Tax Master.`;
      if (this.isTaxableType && this.form.is_manual_tax_rate && (this.form.taxpercent === "" || isNaN(this.form.taxpercent) || Number(this.form.taxpercent) < 0))
        this.errors.taxpercent    = "Enter a valid manual tax percent.";
      return Object.keys(this.errors).length === 0;
    },

    // ── Submit ─────────────────────────────────────────
    postData() {
      if (!this.validate()) {
        Swal.fire({
          title: "Please fix the errors",
          text:  "Some required fields are missing or invalid.",
          icon:  "warning",
        });
        this.$nextTick(() => {
          const el = document.querySelector(".is-invalid");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        return;
      }

      Swal.fire({
        title:              "Save this item?",
        text:               `"${this.form.itemname}" will be added to your inventory.`,
        icon:               "question",
        showCancelButton:   true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor:  "#d33",
        confirmButtonText:  "Yes, save it!",
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        try {
          // Build clean payload — omit empty optional fields
          // NOTE: batchenabled, expiryenabled and image fields excluded (features disabled)
          const payload = {};
          Object.entries(this.form).forEach(([key, val]) => {
            if (val !== "" && val !== null && val !== undefined) {
              payload[key] = val;
            }
          });
          payload.threshold = Number(this.form.minstock || 0);
          payload.maxstock = null;

          const itemResponse = await axios.post("/v1/items", payload);
          const newItemId = itemResponse.data.itemid;
          if (newItemId && this.localImages.length) {
            await this.uploadImages(newItemId);
          }

          await Swal.fire({
            title:             "Item saved!",
            text:              `"${this.form.itemname}" has been added successfully.`,
            icon:              "success",
            timer:             1800,
            showConfirmButton: false,
          });
          this.$router.push({ path: "/productlist" });
        } catch (error) {
          console.error(error);
          const msg = error.response?.data?.detail || error.message;
          Swal.fire({ title: "Error", text: `Could not save item: ${msg}`, icon: "error" });
        }
      });
    },
  },

  // beforeDestroy() {
  //   Image cleanup disabled — future feature
  //   this.localImages.forEach((img) => URL.revokeObjectURL(img.url));
  // },
  beforeDestroy() {
    this.collapsibleSectionCleanups.forEach((cleanup) => cleanup());
    this.collapsibleSectionCleanups = [];
    this.localImages.forEach((img) => {
      if (img?.isNew && img.url) URL.revokeObjectURL(img.url);
    });
  },
};
</script>

<style scoped>
/* ─────────────────────────────────────────────
   SECTION CARD — header + content form ONE seamless
   rounded, shadowed card (matches the reference:
   "Pricing & Stocks" / "Images" / "Custom Fields")
───────────────────────────────────────────── */

/* Outer card wrapper look is built from two adjacent
   elements (header + content) that share a single
   border/shadow footprint and meet at a hairline divider */
.section-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 20px;
  padding: 18px 22px;
  border: 1px solid #e7e9f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.05);
}
.collapsible-section-header {
  cursor: pointer;
  user-select: none;
  transition: background 0.18s ease, border-color 0.18s ease, margin 0.18s ease;
}
.collapsible-section-header:hover {
  background: #fbfbfd;
}
.collapsible-section-header:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(245, 124, 0, 0.14);
}

/* Expanded: header becomes the top edge of the card — flat bottom,
   a single hairline divider, no separate shadow (the content piece
   below carries the shadow so the whole card reads as one shape) */
.collapsible-section-header:not(.is-collapsed) {
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #eef0f5;
  box-shadow: none;
  margin-bottom: 0;
}

/* The content block directly under an expanded header becomes the
   bottom part of the very same card — same side borders, no top
   border (the header's bottom border acts as the sole divider),
   overflow hidden clips any bootstrap .row gutter bleed so the
   rounded corners never look "cut off" or disconnected */
.collapsible-section-header + .row,
.collapsible-section-header + .tax-panel,
.collapsible-section-header + .uom-panel,
.collapsible-section-header + .mb-4 {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 28px !important;
  padding: 22px;
  border: 1px solid #e7e9f0;
  border-top: 0;
  border-radius: 0 0 12px 12px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(17, 24, 39, 0.05);
  overflow: hidden;
}

/* Collapsed: header stands alone as its own rounded, shadowed pill */
.collapsible-section-header.is-collapsed {
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.05);
}

/* Plain chevron icon toggle, top-right of the header — no button
   "pill" background by default, just like the reference screenshot */
.section-toggle-btn {
  margin-left: auto;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #8a8f9c;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s ease, color 0.18s ease;
  flex-shrink: 0;
}
.section-toggle-btn i {
  font-size: 13px;
  transition: transform 0.18s ease;
}
.collapsible-section-header:hover .section-toggle-btn {
  background: #f2f3f7;
  color: #45494f;
}
.collapsible-section-header.is-collapsed .section-toggle-btn i {
  transform: rotate(-90deg);
}

/* Round icon badge — warm accent circle, matching the reference */
.section-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #fff4e8;
  color: #f2872b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  border: 1px solid #ffe3c2;
}
.section-title {
  font-size: 14.5px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
}
.section-sub {
  font-size: 12px;
  color: #8e92a3;
  margin: 0;
  line-height: 1.4;
}

/* Dividers between sections are no longer needed visually since each
   section is now its own card — keep the element for JS logic but hide it */
.section-divider {
  border: none;
  margin: 0;
  height: 0;
}

.tax-rate-status {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.35;
}

.tax-rate-status.loading {
  color: #667085;
}

.tax-rate-status.found {
  color: #117a37;
}

.tax-rate-status.missing {
  color: #d92d20;
}

/* ─────────────────────────────────────────────
   FORM HELPERS
───────────────────────────────────────────── */
.form-label {
  font-size: 13px;
  font-weight: 500;
  color: #3a3f5c;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.required-star {
  color: #e53935;
  font-weight: 700;
}
.optional-tag {
  font-size: 11px;
  font-weight: 400;
  color: #aaa;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 1px 5px;
}
.form-hint {
  font-size: 11px;
  color: #9ea5b8;
  margin-top: 3px;
  display: block;
}

/* ─────────────────────────────────────────────
   INPUT VARIANTS
───────────────────────────────────────────── */
.input-currency { position: relative; }
.input-currency .currency-symbol {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 13px;
  z-index: 1;
  pointer-events: none;
}
.input-currency input { padding-left: 26px !important; }

.input-with-icon { position: relative; }
.input-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #aaa;
  font-size: 13px;
  z-index: 1;
}
.input-with-icon input { padding-left: 32px !important; }

.input-with-suffix { position: relative; }
.input-with-suffix input { padding-right: 30px !important; }
.input-suffix {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 13px;
  pointer-events: none;
}

/* ─────────────────────────────────────────────
   TAX PANEL
───────────────────────────────────────────── */
.tax-panel {
  background: #f9faff;
  border: 1px solid #e8ecff;
  border-radius: 10px;
  padding: 20px;
}
.gst-rate-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.gst-rate-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px;
  border: 1.5px solid #dde1f0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s ease;
  min-width: 80px;
}
.gst-rate-btn:hover  { border-color: #3d5af1; background: #f0f4ff; }
.gst-rate-btn.active { border-color: #3d5af1; background: #3d5af1; color: #fff; }
.rate-label     { font-size: 15px; font-weight: 700; line-height: 1.2; }
.rate-breakdown { font-size: 10px; opacity: 0.75; line-height: 1.3; text-align: center; margin-top: 2px; }
.gst-rate-btn.active .rate-breakdown { opacity: 0.85; }

.gst-breakdown-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.gst-field    { flex: 1; min-width: 100px; max-width: 160px; }
.gst-divider-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 10px;
  color: #aaa;
  font-size: 11px;
  gap: 2px;
}
.gst-divider-arrow i { font-size: 14px; color: #3d5af1; }
.gst-plus { font-size: 20px; font-weight: 300; color: #aaa; padding-bottom: 10px; }
.gst-preview {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 8px 14px;
  text-align: center;
  min-width: 100px;
}
.preview-label { font-size: 11px; color: #388e3c; font-weight: 500; }
.preview-value { font-size: 18px; font-weight: 700; color: #2e7d32; }

/* ─────────────────────────────────────────────
   UOM PANEL — Simplified (matches screenshot)
───────────────────────────────────────────── */
.uom-panel {
  background: #fffbf2;
  border: 1px solid #ffe9c2;
  border-radius: 10px;
  padding: 20px;
}

/* Inline badge labels (UPPER UNIT / LOWER UNIT) */
.uom-badge-inline {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 20px;
  color: #fff;
}
.uom-badge-inline.upper { background: #3d5af1; }
.uom-badge-inline.lower { background: #f57c00; }

/* Conversion row: [Upper chip] = [number input] [Lower chip] */
.uom-conversion-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}
.uom-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 20px;
  white-space: nowrap;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.uom-chip.upper { background: #e8ecff; color: #3d5af1; }
.uom-chip.lower { background: #fff3e0; color: #f57c00; }

.uom-equals {
  font-size: 18px;
  color: #aaa;
  font-weight: 300;
  flex-shrink: 0;
}
.uom-factor-input {
  width: 70px !important;
  text-align: center !important;
  flex-shrink: 0;
}

.uom-example-card {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #2e7d32;
}

/* ─────────────────────────────────────────────
   TOGGLE CARDS — kept for future re-enable
───────────────────────────────────────────── */
.toggle-option-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1.5px solid #e0e3ef;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}
.toggle-option-card:hover  { border-color: #3d5af1; background: #f5f7ff; }
.toggle-option-card.active { border-color: #3d5af1; background: #f0f4ff; }
.toggle-icon {
  width: 36px; height: 36px;
  border-radius: 8px;
  background: #f0f1f5;
  display: flex; align-items: center; justify-content: center;
  color: #666; font-size: 14px; flex-shrink: 0;
  transition: all 0.2s;
}
.toggle-option-card.active .toggle-icon { background: #3d5af1; color: #fff; }
.toggle-content { flex: 1; }
.toggle-title { font-size: 13px; font-weight: 600; color: #1a1a2e; line-height: 1.3; }
.toggle-desc  { font-size: 11px; color: #9ea5b8; margin-top: 2px; }
.toggle-switch-wrap { flex-shrink: 0; }
.toggle-pill {
  width: 38px; height: 22px;
  border-radius: 11px;
  background: #d0d3e0;
  position: relative;
  transition: background 0.2s;
}
.toggle-pill.on { background: #3d5af1; }
.toggle-thumb {
  position: absolute;
  top: 3px; left: 3px;
  width: 16px; height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: transform 0.2s ease;
}
.toggle-pill.on .toggle-thumb { transform: translateX(16px); }

/* ─────────────────────────────────────────────
   IMAGE GRID — kept for future re-enable
───────────────────────────────────────────── */
.image-grid { display: flex; flex-wrap: wrap; gap: 12px; }
.image-tile {
  width: 100px; height: 100px;
  border-radius: 8px; overflow: hidden;
  position: relative;
  border: 1.5px solid #e0e3ef;
}
.image-tile img { width: 100%; height: 100%; object-fit: cover; display: block; }
.image-remove {
  position: absolute; top: 4px; right: 4px;
  width: 22px; height: 22px;
  border-radius: 50%;
  background: rgba(220, 53, 69, 0.9);
  color: #fff; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; padding: 0;
}
.image-add-tile {
  width: 100px; height: 100px;
  border-radius: 8px;
  border: 2px dashed #c5cae9;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 6px; cursor: pointer; color: #9ea5b8;
  font-size: 12px; transition: all 0.18s;
}
.image-add-tile:hover { border-color: #3d5af1; color: #3d5af1; background: #f0f4ff; }
.image-add-tile i { font-size: 20px; }

/* ─────────────────────────────────────────────
   ACTION BUTTONS
───────────────────────────────────────────── */
.form-actions {
  display: flex;
  gap: 12px;
  padding-top: 8px;
}

.unit-field-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}
.unit-field-row :deep(.ui.search.dropdown) {
  flex: 1;
  min-width: 0;
}
.unit-add-btn {
  width: 38px;
  min-width: 38px;
  height: 38px;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  background: #eef2ff;
  color: #4338ca;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
}
.unit-add-btn:hover {
  background: #e0e7ff;
  border-color: #a5b4fc;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
.quick-create-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.quick-create-modal {
  width: min(100%, 560px);
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
}
.quick-create-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.quick-create-modal-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}
.quick-create-modal-subtitle {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.88rem;
}
.quick-create-close {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
}
</style>